import React, { useState, useEffect, useMemo } from 'react';
import { pillarsAPI } from '../services/api';
import { Pillar } from '../types';
import PillarCard from '../components/PillarCard';
import CreatePillarModal from '../components/CreatePillarModal';
// @ts-ignore
import PillarSidePanel from '../components/PillarSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';
import { useDataCache } from '../hooks/useDataCache';
import { usePageLoading } from '../hooks/usePageLoading';
import LoadingOverlay from '../components/LoadingOverlay';
import '../styles/Pillars.css';

const Pillars: React.FC = () => {
  
  // Use caching hook for pillars data with better deduplication
  const {
    data: pillarsData,
    isLoading: pillarsLoading,
    fetchData: fetchPillars,
    clearCache: clearPillarsCache
  } = useDataCache<Pillar[]>({
    cacheKey: 'pillars',
    fetchFunction: pillarsAPI.getAll,
    cacheDuration: 5 * 60 * 1000, // 5 minutes (normal caching)
    autoRefresh: true, // Enable auto-refresh like characters and elements
    preloadCondition: () => true // Always preload pillars
  });

  // Ensure pillars is always an array
  const pillars = pillarsData || [];

  // Force fresh data on mount to ensure we get slugs
  useEffect(() => {
    const forceFreshData = async () => {
      try {
        console.log('🔄 Force clearing pillars cache and fetching fresh data...');
        clearPillarsCache();
        
        // Add a small delay to ensure cache is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
        
        await fetchPillars(true);
        console.log('✅ Fresh pillars data fetched successfully');
      } catch (error) {
        console.error('❌ Failed to fetch fresh pillars data:', error);
      }
    };
    
    forceFreshData();
  }, []); // Run once on mount

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest' | 'level-asc' | 'level-desc'>('level-asc');
  
  // New filter states
  const [selectedL1Pillar, setSelectedL1Pillar] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('article');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPillarPanel, setShowPillarPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedPillar, setSelectedPillar] = useState<Pillar | null>(null);
  const [updatedPillarIds, setUpdatedPillarIds] = useState<Set<string>>(new Set());

  // Use page loading hook - wait for pillars data to load
  const { isLoading: pageLoading } = usePageLoading({
    dependencies: [
      pillarsData !== undefined, // Pillars data has been fetched
      !pillarsLoading // Pillars are no longer loading
    ],
    onLoadComplete: () => {
      console.log('✅ Pillars page loaded successfully');
    }
  });




  // Debug: Log pillars data to check for slug field
  useEffect(() => {
    if (pillars.length > 0) {
      console.log('📊 Pillars loaded:', pillars.length);
      console.log('📊 Pillars with slugs:', pillars.filter((p: Pillar) => p.slug).length);
      console.log('📊 Sample pillar data:', pillars[0]);
      console.log('📊 Sample pillar keys:', Object.keys(pillars[0]));
      console.log('📊 Sample pillar slug value:', pillars[0].slug);
      console.log('📊 All pillar slugs:', pillars.map((p: Pillar) => ({ name: p.name, slug: p.slug })));
    }
  }, [pillars]);



  // Get unique L1 pillars and levels for filter options - optimized with useMemo
  const l1Pillars = useMemo(() => 
    Array.from(new Set(pillars.map((p: Pillar) => p.L1pillar).filter(Boolean))),
    [pillars]
  );
  
  const levels = useMemo(() => 
    Array.from(new Set(pillars.map((p: Pillar) => p.level).filter((level): level is number => level !== undefined))).sort((a: number, b: number) => a - b),
    [pillars]
  );

  // Optimized filtering and sorting with useMemo
  const filteredPillars = useMemo(() => {
    if (!Array.isArray(pillars)) return [];
    
    let filtered = pillars.filter((pillar: Pillar) =>
      pillar && pillar._id && pillar.name && pillar.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply L1 pillar filter
    if (selectedL1Pillar !== 'all') {
      filtered = filtered.filter((pillar: Pillar) => pillar.L1pillar === selectedL1Pillar);
    }

    // Apply level filter
    if (selectedLevel !== 'all') {
      const levelNum = parseInt(selectedLevel);
      filtered = filtered.filter((pillar: Pillar) => pillar.level === levelNum);
    }

    // Sort the filtered pillars
    return filtered.sort((a: Pillar, b: Pillar) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'date-created':
          return new Date(b.createdAt || (b._id ? new Date(b._id).getTime() : 0)).getTime() - new Date(a.createdAt || (a._id ? new Date(a._id).getTime() : 0)).getTime();
        case 'date-created-oldest':
          return new Date(a.createdAt || (a._id ? new Date(a._id).getTime() : 0)).getTime() - new Date(b.createdAt || (b._id ? new Date(b._id).getTime() : 0)).getTime();
        case 'level-asc':
          return (a.level || 0) - (b.level || 0);
        case 'level-desc':
          return (b.level || 0) - (a.level || 0);
        default:
          return 0;
      }
    });
  }, [pillars, searchTerm, sortBy, selectedL1Pillar, selectedLevel]);

  const handlePillarCreated = (newPillar: Pillar) => {
    setShowCreateModal(false);
    
    // Force refresh all pillars to get updated data
    const refreshAllPillars = async () => {
      try {
        console.log('🔄 Clearing cache and refreshing pillars data after creation...');
        clearPillarsCache(); // Clear the cache first
        await fetchPillars(true); // Force refresh with fresh data
        console.log('✅ Pillars data refreshed successfully after creation');
        if (newPillar && newPillar._id) {
          setUpdatedPillarIds(prev => new Set(Array.from(prev).concat(newPillar._id)));
        }
      } catch (error) {
        console.error('Error refreshing pillars:', error);
      }
    };
    
    refreshAllPillars();
  };

  const handleUpdatePillar = (updatedPillar: Pillar) => {
    // Defensive check for undefined or null updatedPillar
    if (!updatedPillar) {
      console.error('❌ handleUpdatePillar received undefined or null updatedPillar');
      return;
    }
    
    // Mark as updated for visual feedback
    if (updatedPillar._id) {
      setUpdatedPillarIds(prev => new Set(Array.from(prev).concat(updatedPillar._id)));
    }
    
    // Clear cache and refresh data immediately for image updates
    const refreshAllPillars = async () => {
      try {
        console.log('🔄 Clearing cache and refreshing pillars data...');
        clearPillarsCache(); // Clear the cache first
        await fetchPillars(true); // Force refresh with fresh data
        console.log('✅ Pillars data refreshed successfully');
      } catch (error) {
        console.error('Error refreshing pillars:', error);
      }
    };
    
    // For image updates, refresh immediately to show the new image
    if (updatedPillar.imageUrl || updatedPillar.image) {
      console.log('🖼️ Image update detected, refreshing immediately...');
      refreshAllPillars();
    } else {
      // For other updates, use requestIdleCallback for better performance
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => refreshAllPillars());
      } else {
        setTimeout(refreshAllPillars, 100);
      }
    }
    
    // Clear the updated flag after a delay
    setTimeout(() => {
      if (updatedPillar && updatedPillar._id) {
        setUpdatedPillarIds(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(updatedPillar._id);
          return newSet;
        });
      }
    }, 1000);
  };

  const handleDeletePillar = (pillarId: string) => {
    // Force refresh all pillars to get updated data
    const refreshAllPillars = async () => {
      try {
        console.log('🔄 Clearing cache and refreshing pillars data after deletion...');
        clearPillarsCache(); // Clear the cache first
        await fetchPillars(true); // Force refresh with fresh data
        console.log('✅ Pillars data refreshed successfully after deletion');
      } catch (error) {
        console.error('Error refreshing pillars:', error);
      }
    };
    
    refreshAllPillars();
  };



  const handleOpenPreviewPanel = (elementIds: string[]) => {
    // Handle preview panel
    console.log('Opening preview for elements:', elementIds);
  };

  // Removed loading UI as per edit hint

  return (
    <>
      {/* Loading Overlay */}
      <LoadingOverlay isLoading={pageLoading} message="Loading pillars..." />
      
      <div className="overview">
        {/* Header - Similar to Overview page */}
        <div className="overview-header">
          <h1 className="overview-title">Pillars</h1>
          <p className="overview-subtitle">Manage your world's pillars</p>
        </div>

        {/* Header with actions - similar to old overview */}
        <div className="overview-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Pillar
          </button>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
                     <select
             value={sortBy}
             onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest' | 'level-asc' | 'level-desc')}
             className="sort-select"
           >
             <option value="date-created">Newest First</option>
             <option value="date-created-oldest">Oldest First</option>
             <option value="name-asc">A to Z</option>
             <option value="name-desc">Z to A</option>
                           <option value="level-asc">Level (Top to Bottom)</option>
              <option value="level-desc">Level (Bottom to Top)</option>
           </select>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          {/* Left side - Filter dropdowns */}
          <div className="filter-left">
            <div className="filter-group">
              <label htmlFor="l1-pillar-filter" className="filter-label">L1 Pillar:</label>
              <select
                id="l1-pillar-filter"
                value={selectedL1Pillar}
                onChange={(e) => setSelectedL1Pillar(e.target.value)}
                className="filter-select"
              >
                <option value="all">All L1 Pillars</option>
                {l1Pillars.map((l1Pillar: string | undefined) => l1Pillar && (
                  <option key={l1Pillar} value={l1Pillar}>
                    {l1Pillar}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="level-filter" className="filter-label">Pillar Level:</label>
              <select
                id="level-filter"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Levels</option>
                {levels.map((level: number) => (
                  <option key={level} value={level.toString()}>
                    Level {level}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn btn--clear-filters"
              onClick={() => {
                setSelectedL1Pillar('all');
                setSelectedLevel('all');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </button>
          </div>

          {/* Right side - Tab selection */}
          <div className="filter-right">
            <div className="filter-group">
              <label htmlFor="tab-filter" className="filter-label">Default Tab:</label>
              <select
                id="tab-filter"
                value={globalTabSelection}
                onChange={(e) => setGlobalTabSelection(e.target.value)}
                className="filter-select tab-select"
              >
                <option value="article">Article</option>
                <option value="ancestors">Ancestors</option>
                <option value="children">Children</option>
                <option value="elements">Elements</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Card Container - Outside overview div */}
      <div className="card-container" id="results-container">
        {Array.isArray(filteredPillars) && filteredPillars.length > 0 ? (
          filteredPillars.map(pillar => pillar && pillar._id ? (
            <PillarCard
              key={`${pillar._id}-${pillar.imageUrl || pillar.image || 'default'}-${updatedPillarIds.has(pillar._id) ? 'updated' : 'stable'}`}
              pillar={pillar}
              onUpdate={handleUpdatePillar}
              onDelete={handleDeletePillar}

              onOpenPreviewPanel={handleOpenPreviewPanel}
              defaultTab={globalTabSelection}
            />
          ) : null)
        ) : (
          <div className="no-results">
            <p>No pillars found.</p>
          </div>
        )}
      </div>

      {/* Create Pillar Modal */}
      {showCreateModal && (
        <CreatePillarModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onPillarCreated={handlePillarCreated}
        />
      )}

      {/* Pillar Side Panel */}
      {showPillarPanel && selectedPillar && (
        <PillarSidePanel
          isOpen={showPillarPanel}
          pillar={selectedPillar}
          onClose={() => {
            setShowPillarPanel(false);
            setSelectedPillar(null);
            setSelectedPillar(null);
          }}
          onUpdate={handleUpdatePillar}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={() => setShowElementPreview(false)}
        />
      )}
    </>
  );
};

export default Pillars; 